import { useEffect, useState } from 'react'
import { Line, LineChart, Bar, BarChart, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'
import type { MentionStatsDailyPoint, MentionStatsBreakdown } from '../lib/api'

// Shared by Dashboard (org-wide rollup, no site_id) and SiteDetail (one site's rollup) —
// same two chart shapes either way, just fed a differently-scoped MentionStats response
// (pkg/api/stats.go's getMentionStats already does the scoping server-side).

function rate(total: number, mentioned: number): number {
  return total > 0 ? Math.round((mentioned / total) * 1000) / 10 : 0
}

// Fixed per-model color + label — used by both the trend chart's lines/legend and its
// tooltip. Distinct from the app's error/positive-indicator colors (#C1666B, #5cc7b8) on
// purpose, except claude's teal, which already meant "positive" elsewhere and reads fine
// here too.
const MODEL_COLORS: Record<string, string> = {
  chatgpt: 'var(--amber)',
  claude: '#5cc7b8',
  gemini: '#7ea6d9',
}
const MODEL_LABELS: Record<string, string> = { chatgpt: 'ChatGPT', claude: 'Claude', gemini: 'Gemini' }

// recharts puts tabindex on its own wrapper for keyboard navigation, so clicking a chart
// leaves the browser's default focus ring drawn as a hard box right around the plot area.
// Suppressed here rather than globally: it's recharts' markup, not ours, and the ring is
// still correct behaviour everywhere else in the app.
const chartFocusReset = '[&_.recharts-wrapper]:outline-none [&_.recharts-surface]:outline-none'

interface PivotedDay {
  date: string
  [key: string]: string | number
}

// pivotDaily reshapes the backend's long format ({date, model, total, mentioned}[]) into
// one row per date with one {model}_rate/{model}_total/{model}_mentioned triple per model
// — what recharts needs to draw one <Line> per model on the same axes. Replaced a single
// dropdown-filtered line plus a separate "Per model" bar chart: switching one model at a
// time made comparing them over time impossible without repeated re-filtering, and having
// both a filter AND a redundant breakdown chart was confusing in its own right. Legend
// click-to-toggle (a recharts built-in) is what replaced the dropdown.
// ALL_MODELS is the pooled series: every answer collected that day, whatever produced it.
//
// Pooled, not the average of the per-model rates. With equal turn counts the two agree, but
// a run that was truncated for credits or lost one language leaves the models with
// different denominators, and an average of rates then weights a model with 2 answers the
// same as one with 15. Pooling is simply "how often were you named", which is the question.
export const ALL_MODELS = 'all'

// Below this many answers, a rate is indicative rather than a measurement.
//
// At n=5 a single answer flipping moves the line 20 points, so a rise from 20% to 40% is
// one answer, not a trend. The chart cannot make the sample bigger, but it can stop
// presenting a number to a precision the sample does not support: points below this are
// drawn hollow and the tooltip says how few answers they rest on.
const LOW_CONFIDENCE_BELOW = 10

function pivotDaily(daily: MentionStatsDailyPoint[]): { rows: PivotedDay[]; models: string[] } {
  const modelsSeen = new Set<string>()
  const byDate = new Map<string, PivotedDay>()
  const pooled = new Map<string, { total: number; mentioned: number }>()
  for (const d of daily) {
    modelsSeen.add(d.model)
    let row = byDate.get(d.date)
    if (!row) {
      row = { date: d.date }
      byDate.set(d.date, row)
    }
    row[`${d.model}_rate`] = rate(d.total, d.mentioned)
    row[`${d.model}_total`] = d.total
    row[`${d.model}_mentioned`] = d.mentioned

    const agg = pooled.get(d.date) ?? { total: 0, mentioned: 0 }
    agg.total += d.total
    agg.mentioned += d.mentioned
    pooled.set(d.date, agg)
  }
  for (const [date, agg] of pooled) {
    const row = byDate.get(date)
    if (!row) continue
    row[`${ALL_MODELS}_rate`] = rate(agg.total, agg.mentioned)
    row[`${ALL_MODELS}_total`] = agg.total
    row[`${ALL_MODELS}_mentioned`] = agg.mentioned
  }
  const rows = Array.from(byDate.values()).sort((a, b) => a.date.localeCompare(b.date))
  // Pooled first so it draws underneath and reads as the headline.
  const models = [ALL_MODELS, ...Array.from(modelsSeen).sort()]
  return { rows, models }
}

function TrendTooltip({ active, payload, label }: { active?: boolean; payload?: { dataKey: string; payload: PivotedDay }[]; label?: string }) {
  if (!active || !payload || payload.length === 0) return null
  return (
    <div className="rounded-lg border border-line bg-card px-3 py-2 text-xs text-ink shadow-lg">
      <div className="mb-1 font-medium">{label}</div>
      {payload.map((entry) => {
        const model = entry.dataKey.replace(/_rate$/, '')
        const total = entry.payload[`${model}_total`]
        const mentioned = entry.payload[`${model}_mentioned`]
        return (
          <div key={model} className="text-ink-soft">
            {model === ALL_MODELS ? 'Alle modellen' : (MODEL_LABELS[model] ?? model)}: {mentioned} van{' '}
            {total} ({entry.payload[`${model}_rate`]}%)
            {Number(total) > 0 && Number(total) < LOW_CONFIDENCE_BELOW && (
              <span className="ml-1 text-amber">weinig metingen</span>
            )}
          </div>
        )
      })}
    </div>
  )
}

export function MentionTrendChart({ daily }: { daily: MentionStatsDailyPoint[] }) {
  // Which series are hidden. Per-model lines start hidden so the pooled answer leads:
  // "am I being mentioned more" is the question, and three overlapping lines answer a
  // different one before it has been asked. Clicking a legend entry brings one back.
  const [hidden, setHidden] = useState<Set<string>>(new Set())
  const [initialised, setInitialised] = useState(false)

  const { rows, models } = pivotDaily(daily)

  useEffect(() => {
    if (initialised || models.length === 0) return
    setHidden(new Set(models.filter((m) => m !== ALL_MODELS)))
    setInitialised(true)
  }, [initialised, models])

  if (daily.length === 0) {
    return <p className="text-ink-soft">Nog geen metingen in deze periode.</p>
  }

  function toggle(model: string) {
    setHidden((prev) => {
      const next = new Set(prev)
      if (next.has(model)) {
        next.delete(model)
      } else {
        next.add(model)
      }
      return next
    })
  }
  return (
    <div className={chartFocusReset}>
    <ResponsiveContainer width="100%" height={240}>
      <LineChart data={rows} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
        <CartesianGrid stroke="var(--line)" strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey="date" tick={{ fill: 'var(--ink-soft)', fontSize: 12 }} tickLine={false} axisLine={{ stroke: 'var(--line)' }} />
        <YAxis
          domain={[0, 100]}
          tickFormatter={(v: number) => `${v}%`}
          tick={{ fill: 'var(--ink-soft)', fontSize: 12 }}
          tickLine={false}
          axisLine={false}
          width={44}
        />
        <Tooltip content={<TrendTooltip />} />
        <Legend
          wrapperStyle={{ fontSize: 12, color: 'var(--ink-soft)' }}
          onClick={(e: { value?: string }) => e.value && toggle(e.value)}
          formatter={(value: string) => (
            <span
              className={`cursor-pointer ${hidden.has(value) ? 'opacity-40' : ''}`}
              title={hidden.has(value) ? 'Klik om te tonen' : 'Klik om te verbergen'}
            >
              {value === ALL_MODELS ? 'Alle modellen' : (MODEL_LABELS[value] ?? value)}
            </span>
          )}
        />
        {/* Straight segments, visible points, and no connectNulls.
            
            This chart used to draw a monotone spline with connectNulls and hidden dots,
            over data that only exists on days a measurement actually ran. Between two
            points a week apart it invented a confident S-curve: a peak at 100% and a dip,
            on days with no measurement at all. For a product whose entire claim is honest
            measurement, a chart that draws the shape it wishes it had measured is the
            worst thing on the page.
            
            Now a reader can see exactly where the evidence is: dots are measurements,
            straight lines join them, and a gap in the series looks like a gap. */}
        {models.map((model) => {
          const isPooled = model === ALL_MODELS
          // hide, rather than not rendering: an unrendered Line takes its legend entry
          // with it, and the toggle becomes one-way with no way to bring the series back.
          return (
            <Line
              key={model}
              type="linear"
              dataKey={`${model}_rate`}
              name={model}
              stroke={isPooled ? 'var(--ink)' : (MODEL_COLORS[model] ?? 'var(--ink-soft)')}
              strokeWidth={isPooled ? 3 : 1.5}
              dot={(props: { cx?: number; cy?: number; payload?: PivotedDay }) => {
                const total = Number(props.payload?.[`${model}_total`] ?? 0)
                const thin = total > 0 && total < LOW_CONFIDENCE_BELOW
                const colour = isPooled ? 'var(--ink)' : (MODEL_COLORS[model] ?? 'var(--ink-soft)')
                return (
                  <circle
                    key={`${model}-${props.cx}-${props.cy}`}
                    cx={props.cx}
                    cy={props.cy}
                    r={isPooled ? 3.5 : 2.5}
                    stroke={colour}
                    strokeWidth={1.5}
                    // Hollow when the sample is too small to be worth reading as a level.
                    fill={thin ? 'var(--card)' : colour}
                  />
                )
              }}
              activeDot={{ r: 5 }}
              connectNulls={false}
              hide={hidden.has(model)}
            />
          )
        })}
      </LineChart>
    </ResponsiveContainer>
    </div>
  )
}

function BreakdownTooltip({ active, payload }: { active?: boolean; payload?: { payload: MentionStatsBreakdown & { rate: number } }[] }) {
  if (!active || !payload || payload.length === 0) return null
  const p = payload[0].payload
  return (
    <div className="rounded-lg border border-line bg-card px-3 py-2 text-xs text-ink shadow-lg">
      <div className="mb-1 font-medium">{p.label}</div>
      <div className="text-ink-soft">
        {p.mentioned} van {p.total} metingen ({p.rate}%)
      </div>
    </div>
  )
}

export function MentionBreakdownChart({ rows }: { rows: MentionStatsBreakdown[] }) {
  if (rows.length === 0) {
    return <p className="text-ink-soft">Nog geen data.</p>
  }
  const data = rows.map((r) => ({ ...r, rate: rate(r.total, r.mentioned) }))
  // 56px/row: confirmed live (screenshot) that anything tighter clips or drops every
  // category label but the last one once there are 2+ rows — recharts' vertical
  // CategoryAxis doesn't reserve space per tick on its own, the container height is what
  // gives each row room to actually render its label, especially the two-line-wrapped
  // ones (long prompt text truncated to 80 chars still wraps within a 120px-wide axis).
  const height = Math.max(90, data.length * 56)
  return (
    <div className={chartFocusReset}>
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} layout="vertical" margin={{ top: 4, right: 24, bottom: 4, left: 4 }}>
        <CartesianGrid stroke="var(--line)" strokeDasharray="3 3" horizontal={false} />
        <XAxis type="number" domain={[0, 100]} tickFormatter={(v: number) => `${v}%`} tick={{ fill: 'var(--ink-soft)', fontSize: 12 }} tickLine={false} axisLine={{ stroke: 'var(--line)' }} />
        <YAxis
          type="category"
          dataKey="label"
          width={120}
          tick={{ fill: 'var(--ink)', fontSize: 12 }}
          tickLine={false}
          axisLine={false}
        />
        <Tooltip content={<BreakdownTooltip />} cursor={{ fill: 'var(--line)', opacity: 0.3 }} />
        <Bar dataKey="rate" fill="var(--amber)" radius={[0, 4, 4, 0]} maxBarSize={18} />
      </BarChart>
    </ResponsiveContainer>
    </div>
  )
}
